import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { Chip, Grid, Icon, makeStyles } from '@material-ui/core';
import type { Theme } from '@material-ui/core';

import { useCurrentUser } from '@klp/auth';
import type { IClient } from '@klp/client/classes';
import { Conditional } from '@klp/conditional';
import { AppRoutes } from '@klp/core/app/config';
import { ActionButton } from '@klp/design';
import { ButtonsContainer } from '@klp/design/ButtonsContainer';
import { Loader } from '@klp/design/Loader';
import type { TableColumnValue } from '@klp/design/Table';
import { Table, TableColumnDef, TableFilterDef } from '@klp/design/Table';
import { ErrorNotification } from '@klp/errorNotification';
import { Search, filterSearchResult } from '@klp/search';

import type { Sak } from '../classes';
import { SakStatusEnum } from '../classes/SakStatus';
import { SakRoutes } from '../config';
import {
    filterSakListByIsArchiveAndMerged,
    openExternalUrl,
} from '../helpers/SakHelpers';
import { useNySak } from '../hooks/useNySak';
import { useGetSakListQuery, useGetSakStatusSummaryQuery } from '../rtkApi';

interface SakListComponentProps {
    archived?: boolean;
    kundeNummerList?: Array<string>;
    selectedClient?: IClient;
    referrer?: 'bygg' | 'sak';
}

const useStyles = makeStyles((theme: Theme) => ({
    chip: {
        margin: theme.spacing(0.5),
    },
}));

export const SakListComponent = ({
    archived,
    referrer,
    selectedClient,
}: SakListComponentProps) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const history = useHistory();
    const classes = useStyles();
    const { byggNummer } = useParams<{
        byggNummer?: string;
    }>();
    const { currentUser, currentUserIsLoading } = useCurrentUser();

    const userEmail = currentUser?.email;
    const kundeNummer = selectedClient?.kundeNummer;

    const sakListParams = useMemo(() => {
        if (!userEmail) {
            return null;
        }

        if (byggNummer) {
            return {
                epost: userEmail,
                byggNummer,
            };
        }

        if (kundeNummer) {
            return {
                kundeNummerList: [kundeNummer],
                epost: userEmail,
            };
        }

        return { epost: userEmail };
    }, [userEmail, byggNummer, kundeNummer]);

    const {
        data: sakListData,
        error: sakListError,
        isLoading: sakListLoading,
    } = useGetSakListQuery(sakListParams!, {
        skip: !sakListParams,
    });

    const { isLoading: statusSummaryLoading } = useGetSakStatusSummaryQuery(
        sakListParams!,
        {
            skip: !sakListParams,
        },
    );

    const sakList = sakListData?.sakList || [];

    const { goToNySak, isLoading: nySakLoading } = useNySak({
        kundeNummer,
        byggNummer,
    });

    const handleSakSelect = (sak: Sak) => {
        if (sak.isDalux) {
            openExternalUrl(sak.eksternUrl!);
            return;
        }

        history.push(SakRoutes.single(sak.id), {
            referrer: referrer,
            byggNummer: byggNummer,
        });
    };

    const handleSearch = (searchTerm: string) => {
        setSearch(searchTerm);
    };

    const handleAddClick = () => {
        goToNySak();
    };

    const renderSakStatus = (value: TableColumnValue): JSX.Element => (
        <Chip
            label={t(`sak.sakStatus.${value}`, { count: 1 })}
            variant="outlined"
            color="secondary"
            className={classes.chip}
        />
    );

    const renderSakKilde = (
        _value: TableColumnValue,
        _column: TableColumnDef<Sak, string>,
        _context: unknown,
        sak: Sak,
    ): JSX.Element =>
        sak.isDalux ? (
            <Chip
                label={t('sak.sourceDalux')}
                icon={<Icon fontSize="small">open_in_new</Icon>}
                variant="outlined"
                className={classes.chip}
            />
        ) : (
            <span />
        );

    const getColumns = (): Array<TableColumnDef<Sak, string>> => {
        const columns = [
            new TableColumnDef({
                translation: 'sak.created',
                key: 'created',
                type: 'date',
                sortable: true,
            }),
            new TableColumnDef({
                translation: 'sak.saksnummer',
                key: 'saksnummer',
                type: 'string',
                sortable: true,
                align: 'left',
            }),
            new TableColumnDef({
                translation: 'sak.emne',
                key: 'emne',
                type: 'string',
                sortable: true,
                align: 'left',
            }),
            new TableColumnDef({
                translation: 'sak.status',
                key: 'status',
                sortable: true,
                align: 'left',
                render: renderSakStatus,
            }),
            new TableColumnDef({
                translation: 'sak.opprettetAv',
                key: 'opprettetAv',
                type: 'string',
                sortable: true,
                align: 'left',
            }),
        ];

        // Kildekolonnen vises bare når listen har Dalux-saker, som i overgangsperioden gjelder få bygg.
        if (sakList.some((sak) => sak.isDalux)) {
            columns.push(
                new TableColumnDef({
                    translation: 'sak.source',
                    key: 'kilde',
                    sortable: true,
                    align: 'left',
                    render: renderSakKilde,
                }),
            );
        }

        if (archived) {
            columns.push(
                new TableColumnDef({
                    translation: 'sak.lukketDato',
                    key: 'lukketDato',
                    type: 'dateTime',
                    sortable: true,
                }),
            );
        }

        return columns;
    };

    const selectedSakList = archived
        ? filterSakListByIsArchiveAndMerged(sakList, archived, false)
        : filterSakListByIsArchiveAndMerged(sakList, archived, true);

    const filteredList = filterSearchResult<Sak>(
        selectedSakList.filter((sak) => sak.status !== SakStatusEnum.SLETTET),
        search,
        ['_created', '_saksnummer', '_opprettetAv', '_emne'],
    );

    return (
        <Loader
            isLoading={
                sakListLoading || currentUserIsLoading || statusSummaryLoading
            }
        >
            <Conditional
                condition={!sakListError}
                errorComponent={
                    <ErrorNotification
                        title="sak.errorDownload.title"
                        description="sak.errorDownload.description"
                        backTarget={AppRoutes.mainPage()}
                        backTitle="general.goBackToFrontpage"
                    />
                }
            >
                <Conditional
                    condition={!sakListLoading || !currentUserIsLoading}
                    errorComponent={
                        <ErrorNotification
                            title="sak.errorNotFound.title"
                            backTarget={AppRoutes.mainPage()}
                            backTitle="general.goBackToFrontpage"
                            mainTitle="sak.addSak"
                            mainClickHandler={handleAddClick}
                        />
                    }
                >
                    <Grid container={true} spacing={2}>
                        <Grid item={true} xs={12} sm={6} md={4} lg={3}>
                            <Search
                                id="sakSearch"
                                searchHandler={handleSearch}
                                searchOnType={true}
                                placeholder="sak.searchSak"
                            />
                        </Grid>
                        <Grid item={true} xs={12} sm={6} md={8} lg={9}>
                            <Grid container={true} justifyContent="flex-end">
                                <Grid item={true}>
                                    <ButtonsContainer>
                                        <ActionButton
                                            actionType="forward"
                                            translation="sak.addSak"
                                            clickHandler={handleAddClick}
                                            disabled={nySakLoading}
                                        />
                                    </ButtonsContainer>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Table
                        id="SakList"
                        emptyTableMessage="sak.noTasks"
                        data={filteredList}
                        filter={
                            !!selectedClient && currentUser
                                ? [
                                      new TableFilterDef<string>({
                                          filterKey: 'opprettetAv',
                                          filterGroup: {
                                              myTasks: [currentUser.email],
                                          },
                                          translations: {
                                              myTasks: 'sak.myTasks',
                                          },
                                      }),
                                  ]
                                : undefined
                        }
                        showAllWhenNoFilter={true}
                        columns={getColumns()}
                        onRowSelect={handleSakSelect}
                    />
                </Conditional>
            </Conditional>
        </Loader>
    );
};
